/**
 * LiveFeed
 * --------
 * Animated scrolling feed of real-time research updates for the hero section.
 * Items scroll upward continuously and loop seamlessly (items are duplicated).
 * Hovering pauses the animation.
 *
 * The feed simulates what a live Labora activity stream looks like —
 * updates, failures, breakthroughs, all timestamped and public.
 */
import styles from './LiveFeed.module.css';

/* Realistic feed items representing recent researcher activity */
const feedItems = [
  {
    id: 1,
    author: 'Elena V.',
    initials: 'EV',
    time: '3 min ago',
    project: 'Microplastics in Alpine Freshwater',
    content:
      'Day 47: pH across all sites is showing a consistent 0.3-unit drop I wasn\'t expecting. Going back to the literature before I draw conclusions.',
    tag: 'Biology',
    color: '#52D68A',
    type: 'update',
  },
  {
    id: 2,
    author: 'James O.',
    initials: 'JO',
    time: '18 min ago',
    project: 'CRISPR Resistance Patterns',
    content:
      'Run #12 failed. Off-target edits at chromosome 14 in the control group. Contamination or genuine signal? Posting full gel images now.',
    tag: 'Microbiology',
    color: '#38BDF8',
    type: 'failure',
  },
  {
    id: 3,
    author: 'Mei L.',
    initials: 'ML',
    time: '41 min ago',
    project: 'Quantum Coherence in Photosynthesis',
    content:
      'Finally got the femtosecond laser calibrated. First clean measurement tomorrow morning. Five months of setup for 200ms of data.',
    tag: 'Physics',
    color: '#A78BFA',
    type: 'milestone',
  },
  {
    id: 4,
    author: 'Arjun S.',
    initials: 'AS',
    time: '1 hr ago',
    project: 'ML Cancer Detection',
    content:
      'Model v0.4 hits 87.3% sensitivity on the validation set. Still getting too many false positives in the 40–50 age cohort. Adjusting class weights.',
    tag: 'Comp. Bio',
    color: '#F59E0B',
    type: 'update',
  },
  {
    id: 5,
    author: 'Sofia R.',
    initials: 'SR',
    time: '2 hr ago',
    project: 'Algal Biodegradable Plastics',
    content:
      'Tensile strength at 42 MPa — comparable to commercial PLA. Three years of experiments and this is the first result I\'m genuinely proud of.',
    tag: 'Chemistry',
    color: '#52D68A',
    type: 'breakthrough',
  },
  {
    id: 6,
    author: 'Lena H.',
    initials: 'LH',
    time: '3 hr ago',
    project: 'Hospital Wastewater Genomics',
    content:
      'Sequencing returned 14 unique resistance gene variants from just two hospitals. This dataset is going to take months to analyze properly.',
    tag: 'Microbiology',
    color: '#38BDF8',
    type: 'update',
  },
  {
    id: 7,
    author: 'Yuki T.',
    initials: 'YT',
    time: '5 hr ago',
    project: 'Perovskite-Silicon Tandems',
    content:
      'Efficiency dropped from 28.1% to 24.6% after 500h damp-heat exposure. Better than expected. Documenting everything in the supplementary log.',
    tag: 'Materials',
    color: '#F59E0B',
    type: 'update',
  },
  {
    id: 8,
    author: 'David N.',
    initials: 'DN',
    time: '6 hr ago',
    project: 'Neuroplasticity Post-TBI',
    content:
      'Paper accepted in Nature Neuroscience. All anonymized imaging data is now on OSF. Three years — thank you to everyone who followed along.',
    tag: 'Neuroscience',
    color: '#38BDF8',
    type: 'breakthrough',
  },
];

/* Map update type to a small label with color */
const typeConfig = {
  update:      { label: 'Update',      color: 'var(--text-muted)' },
  failure:     { label: 'Failure →',   color: '#F87171' },
  milestone:   { label: 'Milestone',   color: '#A78BFA' },
  breakthrough:{ label: 'Breakthrough',color: 'var(--accent-green)' },
};

const FeedItem = ({ item }) => {
  const type = typeConfig[item.type];
  return (
    <div className={styles.item}>
      {/* Author row */}
      <div className={styles.itemHeader}>
        <div
          className={styles.avatar}
          style={{ background: `${item.color}20`, color: item.color }}
        >
          {item.initials}
        </div>
        <div className={styles.headerText}>
          <div className={styles.authorRow}>
            <span className={styles.authorName}>{item.author}</span>
            <span className={styles.separator}>·</span>
            <span className={styles.time}>{item.time}</span>
            <span className={styles.separator}>·</span>
            <span className={styles.typeLabel} style={{ color: type.color }}>{type.label}</span>
          </div>
          <span className={styles.projectName}>{item.project}</span>
        </div>
        <span
          className={styles.tag}
          style={{ background: `${item.color}18`, color: item.color }}
        >
          {item.tag}
        </span>
      </div>
      {/* Content */}
      <p className={styles.content}>{item.content}</p>
    </div>
  );
};

const LiveFeed = () => {
  /* Duplicate items for seamless infinite scroll loop */
  const allItems = [...feedItems, ...feedItems];

  return (
    <div className={styles.wrapper}>
      {/* Live indicator header */}
      <div className={styles.feedHeader}>
        <span className={styles.liveDot} aria-hidden="true" />
        <span className={styles.liveLabel}>Live research feed</span>
        <span className={styles.feedCount}>{feedItems.length} researchers active</span>
      </div>

      {/* Scrolling track */}
      <div className={styles.feedContainer} aria-label="Live research activity feed">
        <div className={styles.feedTrack}>
          {allItems.map((item, idx) => (
            <FeedItem key={`${item.id}-${idx}`} item={item} />
          ))}
        </div>
      </div>

      {/* Fade gradient overlays — top and bottom */}
      <div className={styles.fadeTop}    aria-hidden="true" />
      <div className={styles.fadeBottom} aria-hidden="true" />
    </div>
  );
};

export default LiveFeed;
